/** @jsx jsx */
import { css, jsx } from "@emotion/react"
// React must stay imported: with the emotion `@jsx jsx` pragma and no
// `@jsxFrag`, the `<>` fragment shorthand in the per-user allocation panel
// compiles to `React.Fragment`, so the default React import has to be in scope
// or the component throws "React is not defined" at render.
import React, { useContext, useState, useEffect } from "react"
// import EntityReferenceRevisions from "./DynamicForm/FormTypes/EntityReferenceRevisions"
import NotificationContext from "../../context/Notification/NotificationContext"
import FloatingSelect from "../Elements/Form/FloatingSelect"
import DataContext from "../../context/Data/DataContext"
import ButtonLite from "../Elements/ButtonLite"
import { Tr, Td1, Td2 } from "../Table/TableElements"
import FloatingQuantitySelect from "../Elements/Form/FloatingQuantitySelect"
import IconClose from "../Elements/IconClose"
import { PlusSmIcon } from "@heroicons/react/solid"
import AssetAllocationsTable from "../Table/AssetAllocationsTable"
import UserAllocationPanel from "./UserAllocationPanel"
// This editor is digital-only (physical assets use CreateAssetInventory).
// The stock-capped segment badge (AllocationAvailabilitySummary) is
// intentionally NOT rendered here: the backend has no stock accounting for
// digital-storage, so it reports aloc_available: 0 for every digital segment,
// while this editor models stock as the pseudo-value 999 — the two disagree
// and the badge would flip between 0 and the configured quantity. Re-enable
// once useo2o/o2o-backend#319 defines digital availability semantics.

// Phase 3: per_user_alloc is now a per-segment flag carried inside
// each allocationState row. Normalise truthy values (bool/string/int).
const isTruthyToggle = v => v === true || v === "1" || v === 1

const Allocations = props => {
  const { toggleNotification } = useContext(NotificationContext)
  const {
    allocationState,
    setAllocationState,
    errors,
    userAllocErrors = new Set(),
    total,
  } = props
  // const [create, setCreate] = useState(false)
  // console.log(props, "props")
  const { myUserGroups, universalUserGroups } = useContext(DataContext).data
  const [expandedPanels, setExpandedPanels] = useState(new Set())

  const togglePanel = k => {
    setExpandedPanels(prev => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })
  }

  // const increment = k => {
  //   return
  // }
  // const decrement = k => {
  //   return
  // }

  const hasItems = value => Array.isArray(value) && value.length > 0
  const getGroupType = item => {
    if (hasItems(item?.field_group_companies) || hasItems(item?.companies))
      return "Retail companies"
    if (hasItems(item?.field_group_stores) || hasItems(item?.stores))
      return "Retail stores"
    if (hasItems(item?.field_group_regions) || hasItems(item?.regions))
      return "Regional retailers"
    if (hasItems(item?.field_group_users) || hasItems(item?.users))
      return "Company users"
    return ""
  }

  const groupOptions = [
    ...(Array.isArray(universalUserGroups) ? universalUserGroups : []),
    ...(Array.isArray(myUserGroups) ? myUserGroups : []),
  ].map(item => ({
    value: item.id,
    label: item.title,
    type: getGroupType(item),
  }))

  const groupOptionsById = new Map(
    groupOptions.map(option => [parseInt(option.value), option])
  )

  const addAnother = () => {
    if (
      parseInt(total) === 0 &&
      props.watch()?.field_asset_type?.value === "physical"
    )
      return

    const defaultAllocation = 0

    setAllocationState(prevState => [
      ...prevState,
      {
        roles: [],
        aloc: parseInt(defaultAllocation),
        type: "can_order",
        restriction_number: "5",
        restriction_frequency: "daily",
        // price: "0",
      },
    ])
  }

  const onRemove = k => {
    if (
      parseInt(total) === 0 &&
      props.watch()?.field_asset_type?.value === "physical"
    )
      return
    setAllocationState(prev => {
      const next = JSON.parse(JSON.stringify(prev))
      next.splice(k, 1)
      return next
    })
    // Shift expandedPanels indices after removal.
    setExpandedPanels(prev => {
      const next = new Set()
      prev.forEach(idx => {
        if (idx < k) next.add(idx)
        else if (idx > k) next.add(idx - 1)
      })
      return next
    })
  }

  const updateFieldChanged = (stateIndex, fieldName, fieldValue) => {
    if (
      parseInt(total) === 0 &&
      props.watch()?.field_asset_type?.value === "physical"
    )
      return

    if (fieldName === "aloc") {
      // check that total allocated does not exceed total in stock
      let totalAllocated = 0
      allocationState.forEach((item, k) => {
        if (k !== stateIndex) {
          totalAllocated += parseInt(item.aloc)
        }
      })
      totalAllocated += parseInt(fieldValue)
      if (totalAllocated > parseInt(total)) {
        toggleNotification({
          error: true,
          content: "You've reached the maximum stock available.",
        })

        return
      }

      // Prevent reducing segment qty below sum of per-user allocations.
      if (allocationState[stateIndex]?.user_allocations?.length > 0) {
        const currentUserTotal = allocationState[
          stateIndex
        ].user_allocations.reduce((sum, a) => sum + (parseInt(a.aloc) || 0), 0)
        if (parseInt(fieldValue) < currentUserTotal) {
          toggleNotification({
            error: true,
            content: "Reduce individual user allocations first.",
          })
          return
        }
      }
    }

    setAllocationState(prevState => {
      const newArr = [...prevState]
      // No aloc_available recompute here: digital pseudo-stock (999) must not
      // feed the availability formula. See useo2o/o2o-backend#319.
      newArr[stateIndex] = { ...newArr[stateIndex], [fieldName]: fieldValue }
      return newArr
    })
  }

  const typesOptions = [
    { value: "can_order", label: "Can Order" },
    { value: "must_request", label: "Must Request" },
  ]

  const frequencyOptions = [
    { value: "daily", label: "Per day" },
    { value: "weekly", label: "Per week" },
    // { value: "biweekly", label: "Biweekly" },
    { value: "monthly", label: "Per month" },
    { value: "yearly", label: "Per year" },
  ]

  // Check if a specific allocation row targets an internal segment (Company users).
  const isRowInternal = item => {
    if (!item.roles?.length) return false
    const roleId = parseInt(item.roles[0])

    // Check universal user groups.
    const universalMatch = universalUserGroups.find(
      g => parseInt(g.id) === roleId
    )
    if (universalMatch?.users?.length > 0) return true

    // Check my user groups.
    const myMatch = myUserGroups.find(g => parseInt(g.id) === roleId)
    if (myMatch?.field_group_users?.length > 0 || myMatch?.users?.length > 0)
      return true

    return false
  }

  // Auto-expand per-user panels for internal segments.
  useEffect(() => {
    const internalIndices = new Set()
    allocationState.forEach((item, k) => {
      if (isRowInternal(item)) internalIndices.add(k)
    })
    if (internalIndices.size > 0) {
      setExpandedPanels(prev => {
        const next = new Set(prev)
        internalIndices.forEach(k => next.add(k))
        return next
      })
    }
  }, [allocationState.map(a => a.roles?.[0]).join(",")])

  return (
    <div className="relative">
      {parseInt(total) === 0 &&
        props.watch()?.field_asset_type?.value === "physical" && (
          <div
            className="absolute mb-2 top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,.4)] z-[1] rounded-[8px]"
            css={css`
              backdrop-filter: blur(4px);
            `}
          >
            <div className="bg-white rounded-[8px] px-8 py-10 absolute top-[100px] mx-auto left-0 right-0 w-[50%] shadow-[0_10px_30px_rgba(0,0,0,.2)]">
              <h2 className="text-strong-25 !text-left">Sorry!</h2>
              <p className="text-reg-16 mb-4">
                This item must be in stock before allocation inventory.
              </p>
            </div>
          </div>
        )}
      <div
        className={
          parseInt(total) === 0 &&
          props.watch()?.field_asset_type?.value === "physical"
            ? `p-6 h-[400px] overflow-hidden`
            : `p-0`
        }
      >
        {/* <p className="text-reg-16 mb-8">
            Select 1 or more User Segments and assign their Per Segment
            Availability and Per Location Order Limits. Allocations are
            prioritized from most specific User Segment to least; Users, Stores,
            Companies and then Regions.
          </p> */}
        <AssetAllocationsTable
          columns={[
            "",
            "Per Segment Availability",
            "Order Limits",
            // "Payments",
          ]}
          sm
          overflowVisible
        >
          {allocationState.map((item, k) => {
            const roleId = item.roles?.[0]
            const selectedGroup =
              roleId !== undefined && roleId !== null
                ? groupOptionsById.get(parseInt(roleId))
                : null
            const extraValueLabel = item.roles?.length
              ? `Type : ${selectedGroup?.type || "Unknown"}`
              : undefined

            return (
              <Tr key={k}>
                <Td1 className="min-w-[220px] !pr-0">
                  <div className="flex flex-col">
                    <FloatingSelect
                      label="User Segment"
                      name="User Segment"
                      extraValueLabel={extraValueLabel}
                      options={groupOptions}
                      subtitleSelector="type"
                      value={selectedGroup || null}
                      className={`${
                        !item.roles?.length || errors.has(k)
                          ? "mt-6 !mb-0"
                          : "!mb-0"
                      } `}
                      onChange={v => {
                        const nextRoles = v?.value ? [v.value] : []
                        updateFieldChanged(k, "roles", nextRoles)
                        // Segment changed -> old per-user UIDs no longer
                        // belong to the new segment. Always clear
                        // user_allocations so alien UIDs don't reach the
                        // PATCH payload or trip the FE completeness
                        // validator.
                        updateFieldChanged(k, "user_allocations", [])
                        // When the newly selected segment is not internal,
                        // also clear per_user_alloc so a stale 1 from a
                        // prior internal selection cannot reach the BE
                        // (which 400s on retailer-group segments with
                        // per_user_alloc=1).
                        if (!isRowInternal({ roles: nextRoles })) {
                          updateFieldChanged(k, "per_user_alloc", 0)
                        }
                      }}
                      error={
                        !item.roles?.length || errors.has(k)
                          ? { message: "This field is required." }
                          : false
                      }
                      required={true}
                    />
                  </div>
                </Td1>

                <Td1>
                  <div className="flex space-x-0.5">
                    <FloatingQuantitySelect
                      flush
                      decrement={() => {
                        if (parseInt(item.aloc) > 0)
                          updateFieldChanged(k, "aloc", parseInt(item.aloc) - 1)
                      }}
                      increment={() => {
                        if (true)
                          updateFieldChanged(k, "aloc", parseInt(item.aloc) + 1)
                      }}
                      count={item.aloc}
                      label="Configured qty"
                      onChange={v => {
                        if (isNaN(v)) updateFieldChanged(k, "aloc", parseInt(0))
                        else updateFieldChanged(k, "aloc", parseInt(v))
                      }}
                    />
                    <FloatingSelect
                      flush
                      className="!mb-0"
                      label="Type"
                      name="type"
                      options={typesOptions}
                      onChange={v => {
                        updateFieldChanged(k, "type", v.value || null)
                      }}
                      required={true}
                      value={
                        typesOptions
                          .map(i => i.value)
                          .includes(allocationState[k].type)
                          ? typesOptions.filter(
                              f => allocationState[k].type === f.value
                            )[0]
                          : typesOptions[0]
                      }
                    />
                  </div>
                </Td1>
                <Td1>
                  {userAllocErrors.has(k) && (
                    <div className="text-reg-12 text-red-600 font-medium mb-2 border border-red-500 rounded px-2 py-1 bg-red-50">
                      Per-user allocation incomplete — assign all {item.aloc}{" "}
                      units below.
                    </div>
                  )}
                  {/* W4: Order Limits no longer apply to internal-group
                       segments (sales reps). Render the qty + frequency
                       inputs only for non-internal rows. */}
                  {!isRowInternal(item) && (
                    <div className="flex space-x-0.5">
                      <FloatingQuantitySelect
                        flush
                        decrement={() => {
                          if (parseInt(item.restriction_number) > 0)
                            updateFieldChanged(
                              k,
                              "restriction_number",
                              parseInt(item.restriction_number) - 1
                            )
                        }}
                        increment={() => {
                          if (true)
                            updateFieldChanged(
                              k,
                              "restriction_number",
                              parseInt(item.restriction_number) + 1
                            )
                        }}
                        onChange={v => {
                          if (isNaN(v))
                            updateFieldChanged(
                              k,
                              "restriction_number",
                              parseInt(0)
                            )
                          else
                            updateFieldChanged(
                              k,
                              "restriction_number",
                              parseInt(v)
                            )
                        }}
                        count={parseInt(item.restriction_number)}
                        label={"Qty"}
                      />
                      <FloatingSelect
                        flush
                        className="!mb-0"
                        options={frequencyOptions}
                        onChange={v => {
                          updateFieldChanged(
                            k,
                            "restriction_frequency",
                            v.value
                          )
                        }}
                        value={
                          frequencyOptions
                            .map(i => i.value)
                            .includes(allocationState[k].restriction_frequency)
                            ? frequencyOptions.filter(
                                f =>
                                  allocationState[k].restriction_frequency ===
                                  f.value
                              )[0]
                            : frequencyOptions[0]
                        }
                        label="Frequency"
                        required={true}
                      />
                    </div>
                  )}
                  {!isRowInternal(item) && (
                    <span className="text-reg-11 text-[#999] mt-1">
                      (per location)
                    </span>
                  )}
                  {isRowInternal(item) && (
                    <label className="text-reg-12 flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 border-[#EBEBEB] rounded text-[#222] focus:ring-1 focus:ring-offset-2 focus:ring-[#222]"
                        checked={isTruthyToggle(item.per_user_alloc)}
                        onChange={e => {
                          updateFieldChanged(
                            k,
                            "per_user_alloc",
                            e.target.checked ? 1 : 0
                          )
                          if (!e.target.checked) {
                            updateFieldChanged(k, "user_allocations", [])
                          }
                        }}
                      />
                      <span>Enable per-user allocation for this segment</span>
                    </label>
                  )}
                  {isRowInternal(item) && (
                    <p className="text-reg-11 text-[#999] mt-1 italic">
                      Limit applies per user, not per shipping address.
                    </p>
                  )}
                  {isRowInternal(item) &&
                    isTruthyToggle(item.per_user_alloc) && (
                      <>
                        <div className="mt-2">
                          <label className="text-reg-11 text-[#999] block mb-1">
                            Count orders from (optional)
                          </label>
                          <input
                            type="date"
                            className="text-reg-12 border border-[#EBEBEB] rounded-[8px] px-2 py-1 w-full"
                            value={item.effective_date || ""}
                            onChange={e =>
                              updateFieldChanged(
                                k,
                                "effective_date",
                                e.target.value || null
                              )
                            }
                          />
                          <p className="text-reg-11 text-[#999] mt-0.5 italic">
                            Only orders after this date count toward the
                            per-user limit.
                          </p>
                        </div>
                        <button
                          type="button"
                          className={`text-reg-11 underline mt-1 cursor-pointer ${
                            userAllocErrors.has(k)
                              ? "text-red-600 font-semibold"
                              : "text-[#2e669b]"
                          }`}
                          onClick={() => togglePanel(k)}
                        >
                          {userAllocErrors.has(k) && (
                            <span className="text-red-600 text-reg-14 font-bold mr-1">
                              *
                            </span>
                          )}
                          {expandedPanels.has(k)
                            ? "▼ Hide per-user allocation"
                            : "▶ Customize per-user allocation"}
                        </button>

                        {expandedPanels.has(k) && item.roles?.length > 0 && (
                          <UserAllocationPanel
                            segmentId={item.roles[0]}
                            segmentAloc={parseInt(item.aloc) || 0}
                            totalStock={total}
                            usageVisible={item.usage_visible === true}
                            userAllocations={item.user_allocations || []}
                            hasError={userAllocErrors.has(k)}
                            onChange={updated =>
                              updateFieldChanged(k, "user_allocations", updated)
                            }
                          />
                        )}
                      </>
                    )}
                </Td1>

                {
                  //  <Td2>
                  //   <div className="flex space-x-0.5">
                  //     <FloatingQuantitySelect
                  //       flush
                  //       decrement={() => {
                  //         if (parseInt(item.price) > 0)
                  //           updateFieldChanged(
                  //             k,
                  //             "price",
                  //             parseInt(item.price) - 1
                  //           )
                  //       }}
                  //       increment={() => {
                  //         if (true)
                  //           updateFieldChanged(
                  //             k,
                  //             "price",
                  //             parseInt(item.price) + 1
                  //           )
                  //       }}
                  //       onChange={v => {
                  //         if (isNaN(v))
                  //           updateFieldChanged(k, "price", parseInt(0))
                  //         else updateFieldChanged(k, "price", parseInt(v))
                  //       }}
                  //       count={parseInt(item.price)}
                  //       label={"price*"}
                  //     />
                  //   </div>
                  // </Td2>
                }

                <Td1 className={"p-1"}>
                  <IconClose
                    callback={() => onRemove(k)}
                    small
                    height={2}
                    width={2}
                  />
                </Td1>
              </Tr>
            )
          })}
        </AssetAllocationsTable>

        <div className="mt-3 mb-8 max-w-[200px]">
          {/* <ButtonLite red inline callback={() => addAnother()}>
              Add another Allocation
            </ButtonLite> */}

          <ButtonLite
            type="button"
            inline
            callback={() => addAnother()}
            customContent={
              <div className="pt-5 flex cursor-pointer">
                <div className="w-[26px] h-[26px] text-[#2e669b] !hover:text-[#2e669b] rounded-full flex items-center justify-center border-4 border-[#2e669b]">
                  <PlusSmIcon />
                </div>
                <div className="text-[#2e669b] font-semibold text-lg pl-2">
                  Add Allocation
                </div>
              </div>
            }
            colorHover="#2e669b"
          ></ButtonLite>

          {/* <div className="pt-5 flex cursor-pointer">
              <div className="w-[26px] h-[26px] text-[#2e669b] rounded-full flex items-center justify-center border-4 border-[#2e669b]">
                <PlusSmIcon />
              </div>
              <div className="text-[#2e669b] font-semibold text-lg pl-2">
                Add Allocation
              </div>
            </div> */}
        </div>
      </div>
    </div>
  )
}
export default Allocations
